type ColumnType =
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'email'
  | 'file'
  | 'number'
  | 'password'
  | 'tel'
  | 'text'
  | 'textarea'
  | 'time'
  | 'url';

interface IColumn {
  /**
   * `'tableName:columnName'`
   *
   * @default null
   */
  foreignKeyOf?: string;
  /**
   * @default false
   */
  isEditable?: boolean;
  /**
   * @default false
   */
  isFilterable?: boolean;
  /**
   * @default false
   */
  isRequired?: boolean;
  /**
   * @default false
   */
  isSortable?: boolean;
  /**
   * @default null
   */
  label?: string;
  name: string;
  /**
   * @default 'text'
   */
  type?: ColumnType;
}

const DEFAULT = {
  foreignKeyOf: null,
  isEditable: false,
  isFilterable: false,
  isRequired: false,
  isSortable: false,
  label: null,
  type: 'text' as ColumnType
};

class Column {
  protected foreignKeyOf: string;
  protected isEditable: boolean;
  protected isFilterable: boolean;
  protected isRequired: boolean;
  protected isSortable: boolean;
  protected label: string;
  protected name: string;
  protected type: ColumnType;

  constructor(o: IColumn) {
    this.foreignKeyOf = o.foreignKeyOf || DEFAULT.foreignKeyOf;
    this.isEditable = o.isEditable || DEFAULT.isEditable;
    this.isFilterable = o.isFilterable || DEFAULT.isFilterable;
    this.isRequired = o.isRequired || DEFAULT.isRequired;
    this.isSortable = o.isSortable || DEFAULT.isSortable;
    this.label = o.label || DEFAULT.label;
    this.name = o.name;
    this.type = o.type || DEFAULT.type;
  }

  /**
   * @alias clone (non static)
   * @returns A new instance of `Column` with the same values
   */
  public static clone(o: Column): Column {
    return o.clone();
  }

  /**
   * @returns A new instance of `Column` with the same values
   */
  public clone(): Column {
    return new Column(this.toObject());
  }

  public getFKColumn(): string {
    if (!!this.foreignKeyOf) {
      return this.foreignKeyOf.split(':')[1];
    } else {
      return null;
    }
  }

  public getFKTable(): string {
    if (!!this.foreignKeyOf) {
      return this.foreignKeyOf.split(':')[0];
    } else {
      return null;
    }
  }

  public getForeignKeyOf(): string {
    return this.foreignKeyOf;
  }

  public getIsEditable(): boolean {
    return this.isEditable;
  }

  public getIsFilterable(): boolean {
    return this.isFilterable;
  }

  public getIsRequired(): boolean {
    return this.isRequired;
  }

  public getIsSortable(): boolean {
    return this.isSortable;
  }

  public getLabel(): string {
    return this.label || this.name;
  }

  public getName(): string {
    return this.name;
  }

  public getType(): ColumnType {
    return this.type;
  }

  /**
   * @returns The current *column* as plain object
   */
  public toObject(): IColumn {
    return {
      foreignKeyOf: this.foreignKeyOf,
      isEditable: this.isEditable,
      isFilterable: this.isFilterable,
      isRequired: this.isRequired,
      isSortable: this.isSortable,
      label: this.label,
      name: this.name,
      type: this.type
    };
  }

  /**
   * @returns The current *column* as string
   */
  public toString(): string {
    return JSON.stringify(this.toObject());
  }
}

export { ColumnType, IColumn, Column };
